import { Avatar, Box, Button, CircularProgress, Container, Dialog, DialogContent, DialogTitle, IconButton, Typography, useTheme } from '@mui/material';
import { Close, Person, Visibility } from '@mui/icons-material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
  FrsCaseItem,
  FrsVersionItem,
  frsApi,
  GetFrsDetailResponse,
} from 'src/api/frs';
import { CKEditorLazy } from 'src/common/ckeditor';
import { Tabs } from 'src/common/tabs/tabs';
import useGenericStyles from '../../../../styles/useGenericStyles';
import SimpleBreadCrumb from '../../../widgets/BreadCrumb/SimpleBreadCrumb';
import { FrsDescriptionContent } from './FrsDescriptionContent';
import {
  extractDataArray,
  extractDataObject,
  formatDate,
  getInitials,
  GATE_COLORS,
  MOSCOW_COLORS,
  DEFAULT_GATE_COLOR,
  DEFAULT_MOSCOW_COLOR,
  formatFrsFieldLabel,
  formatMoscowLabel,
} from './frsUtils';

const formatChangeFieldValue = (key: string, value: unknown) => {
  const text = String(value ?? '-');
  if (key === 'moscow') {
    return formatMoscowLabel(text);
  }
  return text;
};

const FRSDetails: FC = () => {
  const classes = useGenericStyles();
  const theme = useTheme();
  const { projectID, frsID } = useParams<{ projectID: string; frsID: string }>();

  const [activeTab, setActiveTab] = useState('tickets');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState<GetFrsDetailResponse | null>(null);
  const [versions, setVersions] = useState<FrsVersionItem[]>([]);
  const [cases, setCases] = useState<FrsCaseItem[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<FrsVersionItem | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!frsID) return;

      try {
        setError('');
        setIsLoading(true);
        const [detailsResponse, versionsResponse, casesResponse] = await Promise.all([
          frsApi.getFrsDetails(frsID),
          frsApi.getFrsVersions(frsID),
          frsApi.getFrsCases(frsID),
        ]);

        setDetails(extractDataObject<GetFrsDetailResponse>(detailsResponse));
        setVersions(extractDataArray<FrsVersionItem>(versionsResponse));
        setCases(extractDataArray<FrsCaseItem>(casesResponse));
      } catch (_e) {
        setError('Failed to load FRS details.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [frsID]);

  const tabs = useMemo(
    () => [
      { label: 'Tickets', value: 'tickets' },
      { label: 'Versions', value: 'versions' },
    ],
    []
  );

  const breadcrumbData = {
    links: [
      {
        name: 'Projects',
        to: '/projects',
      },
      {
        name: details?.projectName,
        to: `/projects/${details?.projectId}/cases`,
      },
      {
        name: details?.frsDisplayKey,
        to: `/projects/${details?.projectId}/frs/${frsID}`,
      },
    ],
  };

  if (isLoading && !details) {
    return (
      <Box mt={2} minHeight={220} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} className={classes.container}>
      {/* Breadcrumb */}
      <Box style={{ marginBottom: '12px' }}>
        <SimpleBreadCrumb data={breadcrumbData} />
      </Box>

      <Box
        p={3}
        border={`1px solid ${theme.palette.divider}`}
        borderRadius="12px"
        bgcolor={theme.palette.background.paper}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
            {details?.frsDisplayKey || '-'} &nbsp; &gt; &nbsp;{' '}
            {details?.frsDisplayKey || '-'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
              Category: {details?.categoryName || '-'}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: GATE_COLORS[details?.gate || ''] || DEFAULT_GATE_COLOR,
                }}
              />
              <Typography variant="body2" style={{ color: GATE_COLORS[details?.gate || ''] || DEFAULT_GATE_COLOR, fontWeight: 500, fontSize: '0.75rem' }}>
                {details?.gate || '-'}
              </Typography>
            </Box>
            <Box
              px={1}
              py={0.25}
              borderRadius={16}
              bgcolor={(MOSCOW_COLORS[details?.moscow || ''] || DEFAULT_MOSCOW_COLOR).bg}
              style={{ border: `1px solid ${(MOSCOW_COLORS[details?.moscow || ''] || DEFAULT_MOSCOW_COLOR).text}33` }}
            >
              <Typography variant="body2" style={{ color: (MOSCOW_COLORS[details?.moscow || ''] || DEFAULT_MOSCOW_COLOR).text, fontWeight: 600, fontSize: '0.75rem' }}>
                {formatMoscowLabel(details?.moscow)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="h4" style={{ fontWeight: 700, marginBottom: 16 }}>
          {details?.title || '-'}
        </Typography>

        <Typography
          variant="h6"
          color="textSecondary"
          style={{ marginBottom: 10, fontSize: '0.75rem', fontWeight: 700 }}>
          Description
        </Typography>

        <Box
          p={2}
          minHeight={96}
          border={`1px solid ${theme.palette.divider}`}
          bgcolor={theme.palette.background.default}
          onClick={() => {
            if (!isEditingDescription) {
              setIsEditingDescription(true);
              setDescriptionValue(details?.description || '');
            }
          }}
          style={{ cursor: isEditingDescription ? 'default' : 'pointer' }}>
          {isEditingDescription ? (
            <Box>
              <CKEditorLazy
                data={descriptionValue}
                onChange={setDescriptionValue}
                placeholder="Enter your Description here"
              />
              <Box mt={2} display="flex" style={{ gap: 8 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={isSavingDescription}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!details || !frsID || !projectID) return;
                    try {
                      setIsSavingDescription(true);
                      await frsApi.updateFrs(frsID, {
                        projectId: projectID,
                        categoryId: details.categoryId,
                        title: details.title,
                        description: descriptionValue,
                        gate: details.gate,
                        moscow: details.moscow,
                        approvalStatus: details.approvalStatus,
                      });
                      setIsEditingDescription(false);
                      // Re-load details to show updated description
                      const detailsResponse = await frsApi.getFrsDetails(frsID);
                      setDetails(extractDataObject<GetFrsDetailResponse>(detailsResponse));
                    } catch (err) {
                      setError('Failed to update description.');
                    } finally {
                      setIsSavingDescription(false);
                    }
                  }}>
                  {isSavingDescription ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Save'
                  )}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditingDescription(false);
                  }}>
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <FrsDescriptionContent
              value={details?.description}
              style={{ color: theme.palette.text.primary }}
            />
          )}
        </Box>
      </Box>

      <Box
        mt={2}
        p={2}
        border={`1px solid ${theme.palette.divider}`}
        borderRadius="12px"
        bgcolor={theme.palette.background.paper}>
        <Tabs
          activeTab={activeTab}
          handleTabChange={(_event: any, value: string) => setActiveTab(value)}
          tabs={tabs}
          isTransform={false}
        />

        {activeTab === 'tickets' && (
          <Box>
            {cases.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: '24px 0' }}>
                No related cases
              </Typography>
            ) : (
              cases.map((item) => (
                <Box key={item.id || item._id} mb={2}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Avatar style={{ width: 20, height: 20 }}>
                      <Person fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" style={{ fontWeight: 700, fontSize: '0.75rem' }}>
                      {item.createdBy?.name || '-'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                      {formatDate(item.createdAt)}
                    </Typography>
                  </Box>
                  <Box
                    p={2}
                    bgcolor={theme.palette.background.default}
                    borderRadius="8px">
                    <Typography variant="body1" style={{ marginBottom: 8, fontSize: '0.75rem' }}>
                      Ticket ID: <strong>{item.caseKey || '-'}</strong>
                    </Typography>
                    <Typography variant="h6" style={{ marginBottom: 8, fontSize: '0.875rem', fontWeight: 700 }}>
                      {item.summary || '-'}
                    </Typography>
                    <Box mb={1.5}>
                      <FrsDescriptionContent value={item.description} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                      <strong>Status:</strong> {item.status?.name || '-'},{' '}
                      <strong>Priority:</strong> {item.priority?.name || '-'}.
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {activeTab === 'versions' && (
          <Box>
            {versions.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: '24px 0' }}>
                No versions available
              </Typography>
            ) : (
              versions.map((item) => (
                <Box key={item.id} mb={2}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Avatar style={{ width: 20, height: 20 }}>
                      {getInitials(item.createdBy?.name || '') || (
                        <Person fontSize="small" />
                      )}
                    </Avatar>
                    <Typography variant="body2" style={{ fontWeight: 700, fontSize: '0.75rem' }}>
                      {item.createdBy?.name || '-'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.75rem' }}>
                      {formatDate(item.createdAt)}
                    </Typography>
                  </Box>
                  <Box
                    p={2}
                    bgcolor={theme.palette.background.default}
                    borderRadius="8px"
                    position="relative">
                    <IconButton
                      size="small"
                      onClick={() => setSelectedVersion(item)}
                      style={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: '#1BB4EB',
                        color: '#fff',
                        padding: 6
                      }}
                    >
                      <Visibility style={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography variant="body1" style={{ marginBottom: 8, fontSize: '0.75rem' }}>
                      Version ID: <strong>{item.versionNumber}</strong>{' '}
                      {item.isCurrent ? <strong>(Current)</strong> : null}
                    </Typography>
                    <Typography variant="body1" style={{ marginBottom: 8, fontSize: '0.75rem' }}>
                      Title: <strong>{item.snapshot?.title || '-'}</strong>
                    </Typography>
                    <Box mb={1.5}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        component="span"
                        style={{ fontSize: '0.75rem', marginRight: 4 }}>
                        Note:
                      </Typography>
                      <FrsDescriptionContent
                        value={item.snapshot?.description}
                      />
                    </Box>
                    <Typography
                      variant="overline"
                      color="textSecondary"
                      style={{ fontWeight: 700 }}>
                      Changes
                    </Typography>
                    {Object.keys(item.changeTo || {}).length === 0 &&
                      Object.keys(item.changeFrom || {}).length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No field changes
                      </Typography>
                    ) : (
                      Object.keys(item.changeTo || {}).map((key) => (
                        <Box key={key} mb={1}>
                          <Typography variant="body2" style={{ fontWeight: 700 }}>
                            {formatFrsFieldLabel(key)}:
                          </Typography>
                          {key === 'description' ? (
                            <Box display="flex" alignItems="flex-start" flexWrap="wrap" style={{ gap: 8 }}>
                              <FrsDescriptionContent value={String(item.changeFrom?.[key] ?? '')} />
                              <Typography variant="body2" component="span">
                                →
                              </Typography>
                              <FrsDescriptionContent value={String(item.changeTo?.[key] ?? '')} />
                            </Box>
                          ) : (
                            <Typography variant="body2">
                              {formatChangeFieldValue(key, item.changeFrom?.[key])} →{' '}
                              {formatChangeFieldValue(key, item.changeTo?.[key])}
                            </Typography>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {error && (
          <Typography
            variant="body2"
            style={{ marginTop: 8, color: theme.palette.error.main }}>
            {error}
          </Typography>
        )}
      </Box>

      <Dialog
        open={Boolean(selectedVersion)}
        onClose={() => setSelectedVersion(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16, padding: 8 },
        }}>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              Change Details
            </Typography>
            <IconButton size="small" onClick={() => setSelectedVersion(null)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          {selectedVersion && (
            <Box mb={2}>
              <Box display="flex" gap={4} mb={3}>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ fontWeight: 700, display: 'block', fontSize: '0.75rem' }}>
                    Changed By
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {selectedVersion.createdBy?.name || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ fontWeight: 700, display: 'block', fontSize: '0.75rem' }}>
                    Date & Time
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {formatDate(selectedVersion.createdAt)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ fontWeight: 700, display: 'block', fontSize: '0.75rem' }}>
                    Version
                  </Typography>
                  <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {selectedVersion.versionNumber}{' '}
                    {selectedVersion.isCurrent ? '(Current)' : ''}
                  </Typography>
                </Box>
              </Box>

              <Box mb={3}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ fontWeight: 700, marginBottom: 8 }}>
                  Previous Value
                </Typography>
                <Box
                  p={2}
                  bgcolor="#FEE8E8"
                  borderRadius={4}
                  border="1px solid #FAD2D2">
                  {Object.keys(selectedVersion.changeFrom || {}).map((key) => (
                    <Box key={key} mb={1}>
                      <Typography
                        variant="caption"
                        style={{ color: '#C5221F', fontWeight: 700 }}>
                        {formatFrsFieldLabel(key)}
                      </Typography>
                      {key === 'description' ? (
                        <FrsDescriptionContent
                          value={String(selectedVersion.changeFrom?.[key] ?? '')}
                          style={{ color: '#C5221F' }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          style={{ color: '#C5221F', fontWeight: 500 }}>
                          {formatChangeFieldValue(key, selectedVersion.changeFrom?.[key])}
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {Object.keys(selectedVersion.changeFrom || {}).length ===
                    0 && (
                      <Typography variant="body2" style={{ color: '#C5221F' }}>
                        -
                      </Typography>
                    )}
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ fontWeight: 700, marginBottom: 8 }}>
                  New Value
                </Typography>
                <Box
                  p={2}
                  bgcolor="#E0F7FA"
                  borderRadius={4}
                  border="1px solid #B2EBF2">
                  {Object.keys(selectedVersion.changeTo || {}).map((key) => (
                    <Box key={key} mb={1}>
                      <Typography
                        variant="caption"
                        style={{ color: '#00838F', fontWeight: 700, fontSize: '0.75rem' }}>
                        {formatFrsFieldLabel(key)}
                      </Typography>
                      {key === 'description' ? (
                        <FrsDescriptionContent
                          value={String(selectedVersion.changeTo?.[key] ?? '')}
                          style={{ color: '#00838F' }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          style={{ color: '#00838F', fontWeight: 500, fontSize: '0.75rem' }}>
                          {formatChangeFieldValue(key, selectedVersion.changeTo?.[key])}
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {Object.keys(selectedVersion.changeTo || {}).length === 0 && (
                    <Typography variant="body2" style={{ color: '#00838F', fontSize: '0.75rem' }}>
                      -
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default FRSDetails;